"use client";

import React, { useId } from "react";
import * as Primitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { Label } from "../../atoms/Label/Label";
import { Text } from "../../atoms/Text/Text";
import { Icon } from "../../atoms/Icon/Icon";
import { cn } from "../../lib/cn";

export interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  placeholder?: string;
  options: SelectFieldOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

// Radix Select wrapped with the same label/control/error-hint layout as
// FormField, since Select's trigger isn't a plain input FormField can clone into.
export function SelectField({
  label,
  placeholder,
  options,
  value,
  defaultValue,
  onValueChange,
  error,
  hint,
  required,
  disabled,
  className = "",
}: SelectFieldProps) {
  const id = useId();
  const message = error ?? hint;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-error-fg" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </Label>
      <Primitive.Root value={value} defaultValue={defaultValue} onValueChange={onValueChange} disabled={disabled}>
        <Primitive.Trigger
          id={id}
          className={cn(
            "flex w-full items-center justify-between rounded-sm border bg-surface-raised px-3 py-2.5 text-sm text-text outline-none",
            "focus:ring-2 focus:ring-accent-600/30",
            "disabled:cursor-not-allowed disabled:bg-surface disabled:text-text-subtle",
            error ? "border-error-border" : "border-border"
          )}
        >
          <Primitive.Value placeholder={placeholder} />
          <Primitive.Icon>
            <Icon icon={ChevronDown} size="sm" className="text-text-subtle" />
          </Primitive.Icon>
        </Primitive.Trigger>
        <Primitive.Portal>
          <Primitive.Content
            position="popper"
            side="bottom"
            sideOffset={4}
            className="z-50 rounded-sm border border-border bg-surface-raised shadow-lg"
          >
            <Primitive.Viewport className="max-h-60 overflow-y-auto p-1">
              {options.map((option) => (
                <Primitive.Item
                  key={option.value}
                  value={option.value}
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-8 py-2 text-sm text-text outline-none data-[highlighted]:bg-accent-50"
                >
                  <Primitive.ItemIndicator className="absolute left-2 flex items-center">
                    <Icon icon={Check} size="sm" className="text-accent-600" />
                  </Primitive.ItemIndicator>
                  <Primitive.ItemText>{option.label}</Primitive.ItemText>
                </Primitive.Item>
              ))}
            </Primitive.Viewport>
          </Primitive.Content>
        </Primitive.Portal>
      </Primitive.Root>
      {message && (
        <Text as="p" size="xs" className={error ? "text-error-fg" : "text-text-subtle"}>
          {message}
        </Text>
      )}
    </div>
  );
}
